
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';

export interface SeoBreadcrumb {
  name: string;
  url: string;
}

export interface SeoRouteData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'service';
  robots?: string;
  author?: string;
  publishedTime?: string;
  breadcrumbs?: SeoBreadcrumb[];
}

export interface BlogPostingSeoData {
  title: string;
  excerpt: string;
  author: string;
  datePublished?: string;
  image?: string;
}

export interface ServiceSeoData {
  title: string;
  desc: string;
  serviceType?: string;
}

export interface FaqSeoItem {
  question: string;
  answer: string;
}

export interface SeoDynamicData {
  blog?: BlogPostingSeoData;
  service?: ServiceSeoData;
  faqItems?: FaqSeoItem[];
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly siteUrl = 'https://www.reve.cloud';
  private readonly siteName = 'Rêve AI';
  private readonly organizationName = 'Rêve Applied AI';
  private readonly defaultImage = `${this.siteUrl}/assets/images/logo-reve.png`;
  private readonly defaultDescription = 'Rêve is a high-end applied AI company specializing in autonomous agentic workflows, intelligent onboarding orchestration, LLM orchestration, and enterprise AI transformation.';
  private readonly defaultKeywords = 'Applied AI, High-End AI Provider, Agentic Workflows, Employee Onboarding Orchestration, Predictive FinOps, Generative AI Consulting, Zero-Trust LLM Orchestration';
  private readonly defaultRobots = 'index, follow, max-snippet:-1, max-image-preview:large';
  private readonly schemaScriptId = 'geo-structured-data';

  private readonly defaultFaqItems: FaqSeoItem[] = [
    {
      question: 'How do Agentic AI Workflows explicitly reduce operational drag?',
      answer: 'Rêve deploys autonomous L0 agents that interface directly with internal APIs to intercept, reason, and resolve complex workflows entirely without human intervention.'
    },
    {
      question: 'What is the functional difference between L0 and L1 AI Support architectures?',
      answer: 'L0 represents fully autonomous AI agents that resolve user requests directly by altering data and executing tasks securely. L1 involves human engineers actively assisted by AI to accelerate time-to-resolution.'
    },
    {
      question: 'Is my proprietary data safe when integrating Large Language Models?',
      answer: 'Rêve engineers strict Zero-Trust AI environments and localized RAG pipelines so corporate intellectual property does not bleed into public large language model training datasets.'
    }
  ];

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly title: Title,
    private readonly meta: Meta
  ) {
    this.injectEnterpriseSeoLinks();
  }

  updateRouteSeo(route: string, routeData?: Partial<SeoRouteData>, dynamicData: SeoDynamicData = {}): void {
    const normalizedRoute = this.normalizeRoute(route);
    const pageTitle = routeData?.title || this.titleFromRoute(normalizedRoute);
    const description = routeData?.description || this.defaultDescription;
    const canonicalUrl = this.buildAbsoluteUrl(normalizedRoute);
    const pageType = routeData?.type || this.inferPageType(normalizedRoute);
    const fullTitle = `Rêve | ${pageTitle}`;
    const imageUrl = this.toPlainUrl(routeData?.image || dynamicData.blog?.image || this.defaultImage);

    this.doc.documentElement.lang = 'en';
    this.title.setTitle(fullTitle);
    this.injectEnterpriseSeoLinks();
    this.updateCanonical(canonicalUrl);

    this.updateMetaTag('description', description);
    this.updateMetaTag('keywords', routeData?.keywords || this.defaultKeywords);
    this.updateMetaTag('robots', routeData?.robots || this.defaultRobots);
    this.updateMetaTag('author', routeData?.author || 'Rêve AI Inc.');

    this.updateMetaTag('og:site_name', this.siteName, true);
    this.updateMetaTag('og:title', fullTitle, true);
    this.updateMetaTag('og:description', description, true);
    this.updateMetaTag('og:type', pageType === 'article' ? 'article' : 'website', true);
    this.updateMetaTag('og:url', canonicalUrl, true);
    this.updateMetaTag('og:image', imageUrl, true);
    this.updateMetaTag('og:locale', 'en_US', true);

    this.updateMetaTag('twitter:card', 'summary_large_image');
    this.updateMetaTag('twitter:title', fullTitle);
    this.updateMetaTag('twitter:description', description);
    this.updateMetaTag('twitter:image', imageUrl);

    this.removeAllMetaTags('article:published_time', true);
    this.removeAllMetaTags('article:author', true);
    if (pageType === 'article') {
      const publishedTime = routeData?.publishedTime || dynamicData.blog?.datePublished;
      const isoDate = this.toIsoDate(publishedTime);
      if (isoDate) {
        this.updateMetaTag('article:published_time', isoDate, true);
      }
      this.updateMetaTag('article:author', routeData?.author || dynamicData.blog?.author || this.organizationName, true);
    }

    this.updateDynamicGEO(normalizedRoute, pageTitle, description, dynamicData, canonicalUrl, routeData?.breadcrumbs);
  }

  updateDynamicGEO(
    route: string,
    pageTitle: string,
    dynamicDescription: string,
    customData: SeoDynamicData = {},
    canonicalUrl = this.buildAbsoluteUrl(route),
    breadcrumbs?: SeoBreadcrumb[]
  ): void {
    this.removeExistingJsonLd();

    const webpageId = this.withFragment(canonicalUrl, 'webpage');
    const graph: Array<Record<string, unknown>> = [
      {
        '@type': 'Organization',
        '@id': `${this.siteUrl}/#organization`,
        name: this.organizationName,
        url: this.siteUrl,
        logo: this.defaultImage,
        description: 'A high-end applied AI company with 12 years of enterprise engineering experience.',
        sameAs: [
          'https://www.linkedin.com/company/revecloud',
          'https://twitter.com/revecloud'
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'AI Delivery / Sales',
          name: 'Rêve AI Engineers',
          availableLanguage: ['English', 'Arabic']
        }
      },
      {
        '@type': 'WebSite',
        '@id': `${this.siteUrl}/#website`,
        name: this.siteName,
        url: this.siteUrl,
        publisher: { '@id': `${this.siteUrl}/#organization` }
      },
      {
        '@type': 'WebPage',
        '@id': webpageId,
        url: canonicalUrl,
        name: `Rêve | ${pageTitle}`,
        description: dynamicDescription,
        isPartOf: { '@id': `${this.siteUrl}/#website` },
        about: { '@id': `${this.siteUrl}/#organization` }
      },
      this.buildBreadcrumbSchema(route, pageTitle, breadcrumbs)
    ];

    if (route.startsWith('/blog/') && customData.blog) {
      graph.push(this.buildBlogPostingSchema(route, customData.blog));
    }

    if (route.startsWith('/services/') && customData.service) {
      graph.push(this.buildServiceSchema(route, customData.service));
    }

    if (route === '/faq' || customData.faqItems?.length) {
      graph.push(this.buildFaqSchema(customData.faqItems || this.defaultFaqItems));
    }

    const schema = {
      '@context': 'https://schema.org',
      '@graph': graph
    };

    const scriptEl = this.doc.createElement('script');
    scriptEl.id = this.schemaScriptId;
    scriptEl.type = 'application/ld+json';
    scriptEl.textContent = JSON.stringify(schema);
    this.doc.head.appendChild(scriptEl);
  }

  private updateMetaTag(name: string, content: string, isProperty = false): void {
    const attr = isProperty ? 'property' : 'name';
    const selector = `${attr}="${name}"`;
    const tag: MetaDefinition = isProperty
      ? { property: name, content }
      : { name, content };

    this.removeDuplicateElements(`meta[${selector}]`);
    this.meta.updateTag(tag, selector);
  }

  private removeAllMetaTags(name: string, isProperty = false): void {
    const attr = isProperty ? 'property' : 'name';
    Array.from(this.doc.head.querySelectorAll(`meta[${attr}="${name}"]`))
      .forEach(element => element.remove());
  }

  private updateCanonical(canonicalUrl: string): void {
    this.removeDuplicateElements('link[rel="canonical"]');
    let canonicalLink = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = this.doc.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
  }

  private injectEnterpriseSeoLinks(): void {
    [
      'https://images.unsplash.com',
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com'
    ].forEach(url => {
      const plainUrl = this.toPlainUrl(url);
      let link = this.doc.head.querySelector<HTMLLinkElement>(`link[rel="preconnect"][href="${plainUrl}"], link[href="${plainUrl}"]`);
      if (!link) {
        link = this.doc.createElement('link');
        this.doc.head.appendChild(link);
      }
      link.setAttribute('rel', 'preconnect');
      link.setAttribute('href', plainUrl);
      if (plainUrl.includes('gstatic')) {
        link.setAttribute('crossorigin', '');
      }
    });

    [
      { lang: 'en', url: 'https://www.reve.cloud' },
      { lang: 'en-ae', url: 'https://www.reve.cloud/ae' },
      { lang: 'en-sa', url: 'https://www.reve.cloud/sa' },
      { lang: 'ar-sa', url: 'https://www.reve.cloud/ar-sa' },
      { lang: 'x-default', url: 'https://www.reve.cloud' }
    ].forEach(ref => {
      const plainUrl = this.toPlainUrl(ref.url);
      let link = this.doc.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${ref.lang}"]`);
      if (!link) {
        link = this.doc.createElement('link');
        this.doc.head.appendChild(link);
      }
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', ref.lang);
      link.setAttribute('href', plainUrl);
    });
  }

  private buildBreadcrumbSchema(route: string, pageTitle: string, breadcrumbs?: SeoBreadcrumb[]): Record<string, unknown> {
    const items = breadcrumbs?.length
      ? breadcrumbs
      : this.buildBreadcrumbsFromRoute(route, pageTitle);

    return {
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: this.toPlainUrl(item.url)
      }))
    };
  }

  private buildBreadcrumbsFromRoute(route: string, pageTitle: string): SeoBreadcrumb[] {
    const breadcrumbs: SeoBreadcrumb[] = [
      { name: 'Home', url: `${this.siteUrl}/` }
    ];

    if (route === '/') {
      return breadcrumbs;
    }

    const segments = route.split('/').filter(Boolean);
    let path = '';
    segments.forEach((segment, index) => {
      path += `/${segment}`;
      breadcrumbs.push({
        name: index === segments.length - 1 ? pageTitle : this.titleCase(segment),
        url: this.buildAbsoluteUrl(path)
      });
    });

    return breadcrumbs;
  }

  private buildBlogPostingSchema(route: string, blog: BlogPostingSeoData): Record<string, unknown> {
    const routeUrl = this.buildAbsoluteUrl(route);
    const schema: Record<string, unknown> = {
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': routeUrl },
      headline: blog.title,
      description: blog.excerpt,
      author: { '@type': 'Organization', name: blog.author || this.organizationName },
      publisher: { '@id': `${this.siteUrl}/#organization` }
    };

    if (blog.image) {
      schema['image'] = this.toPlainUrl(blog.image);
    }

    const isoDate = this.toIsoDate(blog.datePublished);
    if (isoDate) {
      schema['datePublished'] = isoDate;
    }

    return schema;
  }

  private buildServiceSchema(route: string, service: ServiceSeoData): Record<string, unknown> {
    return {
      '@type': 'Service',
      '@id': this.withFragment(this.buildAbsoluteUrl(route), 'service'),
      name: service.title,
      serviceType: service.serviceType || 'Applied AI service',
      provider: { '@id': `${this.siteUrl}/#organization` },
      description: service.desc,
      url: this.buildAbsoluteUrl(route),
      areaServed: [
        { '@type': 'Country', name: 'India' },
        { '@type': 'Country', name: 'United Kingdom' },
        { '@type': 'Country', name: 'Malaysia' },
        { '@type': 'Country', name: 'Singapore' }
      ]
    };
  }

  private buildFaqSchema(items: FaqSeoItem[]): Record<string, unknown> {
    return {
      '@type': 'FAQPage',
      '@id': `${this.siteUrl}/faq/#faq`,
      mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    };
  }

  private removeExistingJsonLd(): void {
    const existingScript = this.doc.getElementById(this.schemaScriptId);
    if (existingScript) {
      existingScript.remove();
    }
  }

  private removeDuplicateElements(selector: string): void {
    const elements = Array.from(this.doc.head.querySelectorAll(selector));
    elements.slice(1).forEach(element => element.remove());
  }

  private inferPageType(route: string): 'website' | 'article' | 'service' {
    if (route.startsWith('/blog/')) {
      return 'article';
    }
    if (route.startsWith('/services/')) {
      return 'service';
    }
    return 'website';
  }

  private buildAbsoluteUrl(route: string): string {
    const normalizedRoute = this.normalizeRoute(route);
    return `${this.siteUrl}${normalizedRoute === '/' ? '' : normalizedRoute}`;
  }

  private normalizeRoute(route: string): string {
    const withoutQuery = (route || '/').split('?')[0].split('#')[0];
    const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
    return withLeadingSlash.replace(/\/+$/, '') || '/';
  }

  private titleFromRoute(route: string): string {
    if (route === '/') {
      return 'Applied AI for Enterprise Transformation';
    }

    const lastSegment = route.split('/').filter(Boolean).pop() || 'Page';
    return this.titleCase(lastSegment);
  }

  private titleCase(value: string): string {
    return value
      .replace(/-/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  private toPlainUrl(value: string): string {
    const markdownTarget = value.match(/\((https?:\/\/[^)]+)\)/);
    if (markdownTarget) {
      return markdownTarget[1];
    }

    const plainUrl = value.replace(/^\[/, '').replace(/\]$/, '').trim();
    if (/^https?:\/\//i.test(plainUrl)) {
      return plainUrl;
    }
    if (plainUrl.startsWith('/')) {
      return `${this.siteUrl}${plainUrl}`;
    }
    return `${this.siteUrl}/${plainUrl.replace(/^\.\//, '')}`;
  }

  private withFragment(url: string, fragment: string): string {
    return `${url}${url.endsWith('/') ? '' : '/'}#${fragment}`;
  }

  private toIsoDate(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }
}
